import { isNotDefined } from '@sgme/fp';

import { monitorTimeout } from '@/api/monitorTimeout.ts';
import type { DealRejected, DealReply, FillReport } from '@/api/signalR/messages/execution.ts';
import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { flexiForwardApi } from '@/store/flexi-forward/api/index.ts';
import { selectFlexiForwardTile } from '@/store/flexi-forward/selectors/tile.ts';
import {
  flexiForwardExecutionRejected,
  flexiForwardExecutionReset,
  flexiForwardExecutionSucceed,
  flexiForwardPending,
  flexiForwardRejectedExecutionReset,
} from '@/store/flexi-forward/slice.ts';
import { fillReportReceived, getExecution, GrowlSlice, type GrowUpdate } from '@/store/growl/slice.ts';
import { updateGrowlApiErrorThunk } from '@/store/growl/updateGrowlApiErrorThunk.ts';
import type { AppDispatch, AppState } from '@/store/store.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import { SECOND_IN_MILLIS } from '@/utils/time.ts';
import { toBase64 } from '@/utils/toBase64.ts';

interface ExecuteFlexiForwardTradeBody {
  rfsId: string;
  quoteId: string;
  email64NotificationList: string;
  userLocalDateTime: string;
  lastQuoteFromBackTimestamp: number;
  lastQuoteReceivedByFrontTimestamp: number;
  executionClickByFrontTimestamp: number;
  way: 'Ask';
}

export interface ExecuteFlexiForwardTradeParams {
  userEmail: string;
  tileId: string;
  quoteId: string;
  rfsId: string;
  accountId: number;
  currency1: string;
  currency2: string;
  notional: number;
  notionalCurrencyIndex: CurrencyIndex;
  userLocalDateTime: string;
  rate: string;
  premium: string;
  premiumCurrencyIndex: CurrencyIndex;
  lastQuoteFromBackTimestamp: number;
  lastQuoteReceivedByFrontTimestamp: number;
}

// TODO ABO, check how to get dates/timestamps correctly
export const executeFlexiForwardTrade = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<unknown, ExecuteFlexiForwardTradeParams>({
    query: (body) => {
      return {
        url: '/fxOption/rfs/execute',
        method: 'POST',
        body: {
          lastQuoteFromBackTimestamp: body.lastQuoteFromBackTimestamp,
          lastQuoteReceivedByFrontTimestamp: body.lastQuoteReceivedByFrontTimestamp,
          executionClickByFrontTimestamp: Date.now(),
          email64NotificationList: toBase64(body.userEmail),
          quoteId: body.quoteId,
          rfsId: body.rfsId,
          userLocalDateTime: body.userLocalDateTime,
          way: 'Ask',
        } as ExecuteFlexiForwardTradeBody,
      };
    },

    async onQueryStarted(request, { queryFulfilled, getState, dispatch }) {
      const { tileId, rfsId } = request;

      const stopMonitor = monitorTimeout(rfsId, dispatch, {
        onExecFail: () => {
          dispatchFlexiForwardExecutionRejected(tileId, rfsId, dispatch);
        },
        onExecSuccess: () => {
          dispatchFlexiForwardExecutionSuccess(tileId, rfsId, dispatch);
        },
        getPendingExecutionDetails: () => {
          const execution = getExecution(getState() as AppState, rfsId);
          if (
            isNotDefined(execution) ||
            execution.type !== 'flexiForwardDealExecution' ||
            execution.data.status !== 'pending'
          ) {
            return undefined;
          }

          return {
            clientId: execution.data.data.accountId,
            currencyPair: `${execution.data.data.currency1}/${execution.data.data.currency2}`,
            productType: execution.data.productName,
            tradeDate: execution.data.data.tradeDate,
          };
        },
      });

      try {
        await queryFulfilled;
      } catch (error) {
        stopMonitor();
        dispatch(updateGrowlApiErrorThunk({ growlId: rfsId, error }));
        dispatchFlexiForwardExecutionRejected(tileId, rfsId, dispatch);
      }
    },

    async onCacheEntryAdded(params, { getState, dispatch, cacheEntryRemoved }) {
      const currentState = getState() as AppState;
      const { tileId } = params;

      const tile = selectFlexiForwardTile(currentState, tileId);

      if (isNotDefined(tile)) {
        console.error('tile not found', tileId);

        return;
      }

      dispatch(
        GrowlSlice.actions.create({
          growlId: params.rfsId,
          growl: {
            type: 'flexiForwardDealExecution',
            data: {
              status: 'pending',
              tileId,
              productName: 'FxFlexiForward',
              data: {
                way: 'Ask',
                currency1: params.currency1,
                currency2: params.currency2,
                notional: params.notional,
                notionalCurrencyIndex: params.notionalCurrencyIndex,
                accountId: params.accountId,
                tradeDate: params.userLocalDateTime,
                dealRate: params.rate,
                premium: params.premium,
                premiumCurrencyIndex: params.premiumCurrencyIndex,
              },
            },
            createdAt: Date.now(),
            options: { closeable: false },
          },
        }),
      );

      dispatch(flexiForwardPending({ tileId, executionId: params.rfsId }));

      const handleReceiveDealReply = buildHandleReceiveDealReply(params, dispatch);
      const handleReceiveDealRejected = buildHandleReceiveDealRejected(params, dispatch);
      const handleReceiveFillReport = buildHandleReceiveFillReport(params, dispatch);

      const signalRConnector = getSignalRConnector();
      signalRConnector.on('ReceiveDealReply', handleReceiveDealReply);
      signalRConnector.on('ReceiveDealRejected', handleReceiveDealRejected);
      signalRConnector.on('ReceiveFillReport', handleReceiveFillReport);

      await cacheEntryRemoved;

      signalRConnector.off('ReceiveDealReply', handleReceiveDealReply);
      signalRConnector.off('ReceiveDealRejected', handleReceiveDealRejected);
      signalRConnector.off('ReceiveFillReport', handleReceiveFillReport);
    },
  });
};

function buildHandleReceiveDealReply(params: ExecuteFlexiForwardTradeParams, dispatch: AppDispatch) {
  return (dealReply: DealReply) => {
    const executionId = dealReply.Execute.ExecutionId;
    const rfsId = params.rfsId;

    if (executionId !== rfsId) {
      return;
    }

    dispatchFlexiForwardExecutionSuccess(params.tileId, rfsId, dispatch);
  };
}

const dispatchFlexiForwardExecutionSuccess = (tileId: string, rfsId: string, dispatch: AppDispatch) => {
  dispatch(
    GrowlSlice.actions.update({
      growlId: rfsId,
      data: { status: 'integrated' },
      options: {
        closeable: true,
      },
    }),
  );

  dispatch(flexiForwardExecutionSucceed({ tileId }));

  setTimeout(() => {
    dispatch(flexiForwardExecutionReset({ tileId }));
  }, 2 * SECOND_IN_MILLIS);
};

function buildHandleReceiveFillReport(params: ExecuteFlexiForwardTradeParams, dispatch: AppDispatch) {
  return (fillReport: FillReport) => {
    const executionId = fillReport.Execute.ExecutionId;
    const rfsId = params.rfsId;

    if (executionId !== rfsId) {
      return;
    }

    dispatch(fillReportReceived({ growlId: executionId, tradeIds: fillReport.BackOfficeReply.TradeIds }));

    dispatch(flexiForwardApi.internalActions.removeMutationResult({ fixedCacheKey: executionId }));

    dispatch(flexiForwardExecutionSucceed({ tileId: params.tileId }));
  };
}

function buildHandleReceiveDealRejected(params: ExecuteFlexiForwardTradeParams, dispatch: AppDispatch) {
  return (rejectedDeal: DealRejected) => {
    const executionId = rejectedDeal.Execute.ExecutionId;
    const rfsId = params.rfsId;

    if (executionId !== rfsId) {
      return;
    }

    const data: GrowUpdate = {
      status: 'rejected',
      errorCode: rejectedDeal.DealReject.ErrorCode,
      multipassErrorCode: rejectedDeal.DealReject.MultipassErrorCode,
      reason: rejectedDeal.DealReject.ErrorMessage,
      sessionId: rejectedDeal.DealReject.MultipassSessionId,
    };
    dispatch(GrowlSlice.actions.update({ growlId: params.rfsId, data, options: { closeable: true } }));
    dispatchFlexiForwardExecutionRejected(params.tileId, executionId, dispatch);
  };
}

const dispatchFlexiForwardExecutionRejected = (tileId: string, rfsId: string, dispatch: AppDispatch) => {
  dispatch(flexiForwardApi.internalActions.removeMutationResult({ fixedCacheKey: rfsId }));
  dispatch(flexiForwardExecutionRejected({ tileId }));
  setTimeout(() => dispatch(flexiForwardRejectedExecutionReset({ tileId })), 2 * SECOND_IN_MILLIS);
};
